import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { UiSettingsContext } from '../context/UiSettingsContext';
import { useLicense } from '../context/LicenseContext';
import { getVisibleNavItems, getQuickActions } from '../config/navigation';
import { getHelpTopics } from '../config/helpContent';
import {
  Search,
  HelpCircle,
  Command,
  ArrowRight,
  Home,
  LayoutDashboard,
  Building2,
  ArrowLeftRight,
  Users,
  UserCog,
  Calendar,
  FlaskConical,
  GraduationCap,
  BarChart3,
  GitBranch,
  PieChart,
  DollarSign,
  Filter,
  TrendingUp,
  FileText,
  BookOpen,
  Settings,
  Zap,
  X,
} from 'lucide-react';

const HELP_STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'can',
  'do',
  'for',
  'how',
  'i',
  'in',
  'is',
  'it',
  'me',
  'my',
  'of',
  'on',
  'the',
  'to',
  'with',
  'you',
]);

const normalizeHelpTokens = (text) => {
  if (!text) return [];
  return text
    .toLowerCase()
    .split(/\s+/)
    .map(token => token.trim())
    .filter(token => token && token.length > 1 && !HELP_STOP_WORDS.has(token));
};

// Icon mapping for navigation items
const NAV_ICONS = {
  home: Home,
  dashboard: LayoutDashboard,
  campus_summary: Building2,
  transfers: ArrowLeftRight,
  position_budgeting: Users,
  personnel_what_if: UserCog,
  planning: Calendar,
  research: FlaskConical,
  tuition_planning: GraduationCap,
  budget_vs_actual: BarChart3,
  budget_scenarios: GitBranch,
  program_margins: PieChart,
  revenue_recognition: DollarSign,
  enrollment_funnel: Filter,
  yield_analysis: TrendingUp,
  reports: FileText,
  courses: BookOpen,
  admin: Settings,
};

/**
 * GlobalSearch - Command palette for quick navigation
 *
 * Activated with Cmd+K (Mac) or Ctrl+K (Windows/Linux)
 * Provides fuzzy search across navigation items, quick actions, and entities
 */
const GlobalSearch = () => {
  const { user } = useContext(UserContext);
  const { settings: uiSettings = {} } = useContext(UiSettingsContext);
  const { isModuleEnabled } = useLicense();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [results, setResults] = useState({ navItems: [], quickActions: [], helpItems: [] });

  const inputRef = useRef(null);
  const listRef = useRef(null);

  const userRoles = useMemo(
    () => (user?.role_slugs || []).map(s => String(s).toLowerCase()),
    [user]
  );
  const isAuthenticated = !!user;

  // Permission options for filtering
  const permissionOptions = useMemo(() => ({
    userRoles,
    isModuleEnabled,
    isAuthenticated,
  }), [userRoles, isModuleEnabled, isAuthenticated]);

  const navConfigItems = uiSettings.nav_config?.items || [];

  // Get all items for empty query (respect UI nav config ordering/visibility)
  const allNavItems = useMemo(() => {
    const visibleNav = getVisibleNavItems(permissionOptions);
    if (!navConfigItems.length) return visibleNav;

    const configMap = new Map(navConfigItems.map(item => [item.key, item]));
    return visibleNav
      .map((item, index) => {
        const config = configMap.get(item.key);
        if (config && config.visible === false) return null;
        return { ...item, _order: config?.order ?? index };
      })
      .filter(Boolean)
      .sort((a, b) => a._order - b._order)
      .map(({ _order, ...item }) => item);
  }, [permissionOptions, navConfigItems]);

  const allQuickActions = useMemo(
    () => getQuickActions(permissionOptions),
    [permissionOptions]
  );
  const allHelpTopics = useMemo(
    () => getHelpTopics({ userRoles, isModuleEnabled }),
    [userRoles, isModuleEnabled]
  );

  // Flatten results for keyboard navigation
  const flatResults = [
    ...results.navItems.map(item => ({ ...item, type: 'nav' })),
    ...results.quickActions.map(item => ({ ...item, type: 'action' })),
    ...results.helpItems.map(item => ({ ...item, type: 'help' })),
  ];
  const navCount = results.navItems.length;
  const actionCount = results.quickActions.length;
  const helpOffset = navCount + actionCount;

  // Update results when query or permissions change
  useEffect(() => {
    const normalizedQuery = query.toLowerCase().trim();
    if (!normalizedQuery) {
      // Show all items when no query
      setResults({
        navItems: allNavItems.slice(0, 8),
        quickActions: allQuickActions.slice(0, 4),
        helpItems: [],
      });
    } else {
      const matchesQuery = (item) => {
        const searchableText = [
          item.label,
          item.description,
          ...(item.keywords || []),
        ].join(' ').toLowerCase();
        return searchableText.includes(normalizedQuery);
      };
      const helpTokens = normalizeHelpTokens(normalizedQuery);
      const matchesHelpQuery = (item) => {
        if (!helpTokens.length) return true;
        const searchableText = [
          item.label,
          item.description,
          ...(item.keywords || []),
          ...(item.steps || []),
        ].join(' ').toLowerCase();
        return helpTokens.every(token => searchableText.includes(token));
      };

      setResults({
        navItems: allNavItems.filter(matchesQuery),
        quickActions: allQuickActions.filter(matchesQuery),
        helpItems: allHelpTopics.filter(matchesHelpQuery),
      });
    }
    setSelectedIndex(0);
  }, [query, allNavItems, allQuickActions, allHelpTopics]);

  // Handle keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K or Ctrl+K to toggle the search dialog
      // This is a standard UX pattern (VS Code, Slack, etc.) and works even in input fields
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Listen for custom event to close GlobalSearch (used by onboarding spotlight)
  useEffect(() => {
    const handleCloseSearch = () => setIsOpen(false);
    document.addEventListener('closeGlobalSearch', handleCloseSearch);
    return () => document.removeEventListener('closeGlobalSearch', handleCloseSearch);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Handle keyboard navigation within the dialog
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < flatResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : flatResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (flatResults[selectedIndex]) {
          handleSelect(flatResults[selectedIndex]);
        }
        break;
      default:
        break;
    }
  }, [isOpen, flatResults, selectedIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      selectedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleSelect = (item) => {
    setIsOpen(false);
    if (item.type === 'help') {
      navigate(`/help?topic=${encodeURIComponent(item.key)}`);
      return;
    }
    if (item.path.startsWith('/admin#')) {
      window.open(item.path.replace('#', ''), '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.path);
    }
  };

  const getIcon = (item) => {
    if (item.type === 'help') {
      return <HelpCircle className="w-4 h-4" />;
    }
    if (item.type === 'action') {
      return <Zap className="w-4 h-4" />;
    }
    const IconComponent = NAV_ICONS[item.icon] || NAV_ICONS[item.key];
    return IconComponent ? <IconComponent className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />;
  };

  // Don't render if not authenticated
  if (!isAuthenticated) return null;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Dialog */}
      {isOpen && (
        <div
          className="fixed inset-x-2 sm:inset-x-4 top-16 sm:top-24 z-50 mx-auto max-w-xl"
          role="dialog"
          aria-modal="true"
          aria-label="Global search"
          onKeyDown={handleKeyDown}
        >
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            {/* Search input */}
            <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search pages, actions, help..."
                className="flex-1 min-w-0 bg-transparent border-none outline-none text-slate-800 dark:text-slate-200 placeholder-slate-400 text-base"
                aria-label="Search"
              />
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close search"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* Results */}
            <div
              ref={listRef}
              className="max-h-[60vh] sm:max-h-80 overflow-y-auto py-2"
              role="listbox"
            >
              {flatResults.length === 0 ? (
                <div className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                  No results found for "{query}"
                </div>
              ) : (
                <>
                  {/* Navigation items */}
                  {results.navItems.length > 0 && (
                    <div>
                      <div className="px-4 py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Pages
                      </div>
                      {results.navItems.map((item, index) => {
                        const globalIndex = index;
                        return (
                          <button
                            key={item.key}
                            data-index={globalIndex}
                            onClick={() => handleSelect({ ...item, type: 'nav' })}
                            onMouseEnter={() => setSelectedIndex(globalIndex)}
                            className={`w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors ${
                              selectedIndex === globalIndex
                                ? 'bg-blue-50 dark:bg-blue-900/30'
                                : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                            }`}
                            role="option"
                            aria-selected={selectedIndex === globalIndex}
                          >
                            <span className={`p-1.5 rounded-lg ${
                              selectedIndex === globalIndex
                                ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                            }`}>
                              {getIcon(item)}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                {item.label}
                              </div>
                              {item.description && (
                                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {item.description}
                                </div>
                              )}
                            </div>
                            <ArrowRight className={`w-4 h-4 ${
                              selectedIndex === globalIndex
                                ? 'text-blue-500'
                                : 'text-slate-300 dark:text-slate-600'
                            }`} />
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Quick actions */}
                  {results.quickActions.length > 0 && (
                    <div className={results.navItems.length > 0 ? 'mt-2 pt-2 border-t border-slate-200 dark:border-slate-700' : ''}>
                      <div className="px-4 py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Quick Actions
                      </div>
                      {results.quickActions.map((item, index) => {
                        const globalIndex = navCount + index;
                        return (
                          <button
                            key={item.key}
                            data-index={globalIndex}
                            onClick={() => handleSelect({ ...item, type: 'action' })}
                            onMouseEnter={() => setSelectedIndex(globalIndex)}
                            className={`w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors ${
                              selectedIndex === globalIndex
                                ? 'bg-blue-50 dark:bg-blue-900/30'
                                : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                            }`}
                            role="option"
                            aria-selected={selectedIndex === globalIndex}
                          >
                            <span className={`p-1.5 rounded-lg ${
                              selectedIndex === globalIndex
                                ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                            }`}>
                              <Zap className="w-4 h-4" />
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                {item.label}
                              </div>
                              {item.description && (
                                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {item.description}
                                </div>
                              )}
                            </div>
                            <ArrowRight className={`w-4 h-4 ${
                              selectedIndex === globalIndex
                                ? 'text-blue-500'
                                : 'text-slate-300 dark:text-slate-600'
                            }`} />
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {results.helpItems.length > 0 && (
                    <div className={(results.navItems.length > 0 || results.quickActions.length > 0) ? 'mt-2 pt-2 border-t border-slate-200 dark:border-slate-700' : ''}>
                      <div className="px-4 py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Help
                      </div>
                      {results.helpItems.map((item, index) => {
                        const globalIndex = helpOffset + index;
                        return (
                          <button
                            key={item.key}
                            data-index={globalIndex}
                            onClick={() => handleSelect({ ...item, type: 'help' })}
                            onMouseEnter={() => setSelectedIndex(globalIndex)}
                            className={`w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors ${
                              selectedIndex === globalIndex
                                ? 'bg-blue-50 dark:bg-blue-900/30'
                                : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                            }`}
                            role="option"
                            aria-selected={selectedIndex === globalIndex}
                          >
                            <span className={`p-1.5 rounded-lg ${
                              selectedIndex === globalIndex
                                ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                            }`}>
                              {getIcon({ ...item, type: 'help' })}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                {item.label}
                              </div>
                              {item.description && (
                                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {item.description}
                                </div>
                              )}
                            </div>
                            <ArrowRight className={`w-4 h-4 ${
                              selectedIndex === globalIndex
                                ? 'text-blue-500'
                                : 'text-slate-300 dark:text-slate-600'
                            }`} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer with keyboard hints and help link */}
            <div className="px-3 sm:px-4 py-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
              <div className="hidden sm:flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800">↑↓</kbd>
                  navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800">↵</kbd>
                  select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800">esc</kbd>
                  close
                </span>
              </div>
              <span className="sm:hidden text-slate-400">Tap to select</span>
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/help');
                }}
                className="flex items-center gap-1.5 px-3 py-2 sm:px-2 sm:py-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors min-h-[44px] sm:min-h-0"
              >
                <HelpCircle className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                <span>Help</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalSearch;
